import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Tab, Tabs } from '@mui/material';
import { Inventory2Outlined as EmptyIcon, SearchOff as NotFoundIcon } from '@mui/icons-material';
import { notify } from '../../notifications';
import { apiBlobError, apiError, downloadBlob } from '../Documents/format';
import { fmtInt } from '../OnlineJournal/format';
import { useLoad } from '../OnlineJournal/hooks/useLoad';
import { EditEmptyState } from '../reports/edit/EditCommon';
import DataFreshness from './components/DataFreshness';
import PharmaciesTable from './components/PharmaciesTable';
import PharmacyDialog from './components/PharmacyDialog';
import ProductsPanel from './components/ProductsPanel';
import RemainsFilters from './components/RemainsFilters';
import RemainsMap from './components/RemainsMap';
import RemainsSummary from './components/RemainsSummary';
import RemainsToolbar from './components/RemainsToolbar';
import { RemainsMeta, RemainsOrigin, RemainsSelf, SearchQuery, isCancelled, isForbidden, remainsApi } from './remainsApi';
import {
  DEFAULT_PHARMACY_SORT, DEFAULT_PRODUCT_SORT, EMPTY_PHARMACY_FILTERS, Origin, PHARMACY_DESC_FIRST, PRODUCT_DESC_FIRST,
  PharmacyFilterState, PharmacySort, PharmacySortKey, ProductSort, ProductSortKey,
  buildPharmacyRows, buildProductRows, countOptions, filterPharmacyRows, sortPharmacyRows, sortProductRows, summarize
} from './remainsCalc';
import { tabsSx } from './remainsStyles';

interface MetaError { text: string; forbidden: boolean }

type ResultTab = 'pharmacies' | 'products' | 'map';

/** Пока 1С загружает остатки, прогресс опрашивается тихо (/meta в журнал не пишется) */
const META_POLL_MS = 10000;

const selfOrigin = (self: RemainsSelf): Origin => ({ lat: self.lat, lng: self.lng, label: 'Моё место', guid: null });

/**
 * «Остатки» (справочная): в каких аптеках зоны есть товар, сколько, когда аптека работает и как до неё добраться.
 * Фильтры списка аптек общие для таблицы, сводки, карты и выгрузки; выбор товаров сужает аптеки до выбранных.
 */
const RemainsModule: React.FC = () => {
  const [meta, setMeta] = useState<RemainsMeta | null>(null);
  const [metaError, setMetaError] = useState<MetaError | null>(null);
  const [query, setQuery] = useState<SearchQuery | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [tab, setTab] = useState<ResultTab>('pharmacies');
  const [filters, setFilters] = useState<PharmacyFilterState>(EMPTY_PHARMACY_FILTERS);
  const [origin, setOrigin] = useState<Origin | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [folder, setFolder] = useState<string | null>(null);
  const [pharmacySort, setPharmacySort] = useState<PharmacySort>(DEFAULT_PHARMACY_SORT);
  const [productSort, setProductSort] = useState<ProductSort>(DEFAULT_PRODUCT_SORT);
  const [openGuid, setOpenGuid] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const searchKey = query ? JSON.stringify(query) : '';
  const search = useLoad(
    meta && query ? (silent: boolean, signal: AbortSignal) => remainsApi.search(query, silent, signal) : null,
    searchKey, reloadToken, 'Не удалось найти остатки'
  );
  // данные прежнего запроса остаются на экране, только пока грузится новый
  const data = search.stale && !search.loading ? null : search.data;

  useEffect(() => {
    document.title = 'Остатки | Zerde';
  }, []);

  // значения по умолчанию ставятся один раз: фоновые обновления meta не перебивают выбор оператора
  const defaultsApplied = useRef(false);
  /** quiet - фоновое обновление: прежние данные и экран ошибки не трогаются */
  const loadMeta = useCallback((signal?: AbortSignal, quiet = false) => {
    if (!quiet) setMetaError(null);
    return remainsApi.meta(signal)
      .then(res => {
        setMeta(res);
        if (defaultsApplied.current) return;
        defaultsApplied.current = true;
        // регион аккаунта и «Моё место» - если они есть; «Сброс» всё равно очищает фильтры полностью
        const region = res.region;
        if (region) setFilters(f => ({ ...f, region }));
        if (res.self) setOrigin(selfOrigin(res.self));
      })
      .catch(err => {
        if (isCancelled(err) || quiet) return;
        setMetaError({ text: apiError(err, 'Не удалось загрузить остатки'), forbidden: isForbidden(err) });
      });
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    loadMeta(controller.signal);
    return () => controller.abort();
  }, [loadMeta]);

  const syncing = Boolean(meta && meta.data && meta.data.isLoading);
  useEffect(() => {
    if (!syncing) return undefined;
    const controller = new AbortController();
    const timer = window.setInterval(() => { loadMeta(controller.signal, true); }, META_POLL_MS);
    return () => {
      window.clearInterval(timer);
      controller.abort();
    };
  }, [syncing, loadMeta]);

  // загрузка из 1С закончилась - открытый поиск перечитывается по новым данным (тихо, без новой записи в журнале)
  const wasSyncing = useRef(false);
  useEffect(() => {
    if (wasSyncing.current && !syncing) setReloadToken(t => t + 1);
    wasSyncing.current = syncing;
  }, [syncing]);

  // новый запрос - выбор товаров, папка и карточка относятся к прежнему
  useEffect(() => {
    setSelected([]);
    setFolder(null);
    setOpenGuid(null);
  }, [searchKey]);

  // появилась точка отсчёта - ближайшие аптеки сверху; убрали - сортировка по расстоянию теряет смысл
  const hasOrigin = origin !== null;
  useEffect(() => {
    setPharmacySort(s => {
      if (hasOrigin) return { key: 'distance', dir: 'asc' };
      return s.key === 'distance' ? DEFAULT_PHARMACY_SORT : s;
    });
  }, [hasOrigin]);

  const keyRef = useRef(searchKey);
  keyRef.current = searchKey;
  /** Тот же запрос ещё раз - перечитать данные (silent), иначе - новый поиск */
  const runSearch = useCallback((q: SearchQuery) => {
    if (JSON.stringify(q) === keyRef.current) setReloadToken(t => t + 1);
    else setQuery(q);
  }, []);

  const patchFilters = useCallback((patch: Partial<PharmacyFilterState>) => setFilters(f => ({ ...f, ...patch })), []);
  const resetFilters = useCallback(() => {
    setFilters(EMPTY_PHARMACY_FILTERS);
    setOrigin(null);
  }, []);

  // «Моё место» - сохранённая геоточка оператора: браузерная геолокация с ПК неточна
  const self = meta && meta.self ? meta.self : null;
  const locate = useCallback(() => {
    // повторное нажатие на активную кнопку убирает точку отсчёта
    if (origin && !origin.guid) {
      setOrigin(null);
      return;
    }
    if (self) setOrigin(selfOrigin(self));
  }, [origin, self]);

  const pickOrigin = useCallback((p: RemainsOrigin | null) => {
    setOrigin(p ? { lat: p.lat, lng: p.lng, label: p.name, guid: p.guid } : null);
  }, []);

  const sortPharmacies = useCallback((key: PharmacySortKey) => setPharmacySort(s => (
    s.key === key
      ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' }
      : { key, dir: PHARMACY_DESC_FIRST.indexOf(key) !== -1 ? 'desc' : 'asc' }
  )), []);
  const sortProducts = useCallback((key: ProductSortKey) => setProductSort(s => (
    s.key === key
      ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' }
      : { key, dir: PRODUCT_DESC_FIRST.indexOf(key) !== -1 ? 'desc' : 'asc' }
  )), []);

  const toggleProduct = useCallback((zid: string) => setSelected(s => (
    s.indexOf(zid) === -1 ? s.concat(zid) : s.filter(z => z !== zid)
  )), []);
  const clearSelected = useCallback(() => setSelected([]), []);
  const changeTab = useCallback((_: React.SyntheticEvent, value: ResultTab) => setTab(value), []);
  const closePharmacy = useCallback(() => setOpenGuid(null), []);

  // права вычисляются до ранних return: от них зависят расчёты ниже
  // в списках, сводке и на карте цена не выводится - только сумма остатка (своё право, работает только вместе с ценами)
  const withSums = Boolean(meta && meta.permissions.sums);
  const withContacts = Boolean(meta && meta.permissions.contacts);
  const withKm = Boolean(meta && meta.permissions.kmDetails);

  /** Аптеки по всем найденным товарам: до фильтров (регионы) и после (зона для итогов по товарам) */
  const allRows = useMemo(() => (data ? buildPharmacyRows(data, [], origin, withSums) : []), [data, origin, withSums]);
  const allowedRows = useMemo(
    () => filterPharmacyRows(allRows, filters, origin, withContacts),
    [allRows, filters, origin, withContacts]
  );
  const productRowsAll = useMemo(() => {
    if (!data) return [];
    const allowed = allowedRows.length !== allRows.length ? new Set(allowedRows.map(r => r.pharmacy.guid)) : null;
    return buildProductRows(data, withSums, allowed);
  }, [data, allRows, allowedRows, withSums]);
  const productRows = useMemo(() => sortProductRows(productRowsAll, folder, productSort), [productRowsAll, folder, productSort]);

  /** Аптеки по выбранным товарам (ничего не выбрано - по всем найденным) */
  const selectedRows = useMemo(
    () => (data && selected.length ? buildPharmacyRows(data, selected, origin, withSums) : allRows),
    [data, selected, origin, withSums, allRows]
  );
  const pharmacyRows = useMemo(
    () => sortPharmacyRows(filterPharmacyRows(selectedRows, filters, origin, withContacts), pharmacySort),
    [selectedRows, filters, origin, withContacts, pharmacySort]
  );
  const summary = useMemo(() => (data ? summarize(pharmacyRows, withSums) : null), [data, pharmacyRows, withSums]);

  const regions = useMemo(() => {
    const list = countOptions(allRows.map(r => r.pharmacy.region), 'Без региона');
    // выбранный регион остаётся в списке, даже если в новом поиске его аптек нет
    if (filters.region && !list.some(o => o.value === filters.region)) list.push({ value: filters.region, label: filters.region, count: 0 });
    return list;
  }, [allRows, filters.region]);
  const folders = useMemo(() => countOptions(productRowsAll.map(r => r.product.folder), 'Без папки'), [productRowsAll]);
  const origins = meta ? meta.origins : [];

  const exportExcel = useCallback(async () => {
    if (!query) return;
    setExporting(true);
    try {
      const file = await remainsApi.exportFile(query, {
        region: filters.region,
        openNow: filters.openNow,
        missing: filters.missing,
        zids: selected,
        near: origin ? { lat: origin.lat, lng: origin.lng } : null,
        radius: origin ? filters.radius : null
      });
      downloadBlob(file.filename, file.blob);
    } catch (err) {
      if (!isCancelled(err)) notify.error(await apiBlobError(err, 'Не удалось выгрузить остатки'));
    } finally {
      setExporting(false);
    }
  }, [query, filters, selected, origin]);

  const refreshData = useCallback(async () => {
    setRefreshing(true);
    try {
      const res = await remainsApi.refresh();
      if (res.started) notify.success(res.message || 'Загрузка остатков из 1С запущена');
      else notify.warning(res.message || 'Загрузка остатков из 1С уже идёт');
      await loadMeta(undefined, true);
    } catch (err) {
      notify.error(apiError(err, 'Не удалось запустить загрузку из 1С'));
    } finally {
      setRefreshing(false);
    }
  }, [loadMeta]);

  if (metaError) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert
          severity={metaError.forbidden ? 'warning' : 'error'}
          action={metaError.forbidden ? undefined : <Button color="inherit" size="small" onClick={() => { loadMeta(); }}>Повторить</Button>}
        >
          {metaError.forbidden ? 'У вас нет доступа к остаткам. Обратитесь к администратору.' : metaError.text}
        </Alert>
      </Box>
    );
  }

  if (!meta) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress size={32} /></Box>;
  }

  const loading = search.loading;
  const hasListFilters = Boolean(filters.region || filters.openNow || filters.missing || (origin && filters.radius));
  const found = Boolean(data && data.products.length);

  let body: React.ReactNode = null;
  if (!query) {
    body = (
      <EditEmptyState
        icon={<EmptyIcon />} title="Найдите товар"
        text="Название, штрихкод, артикул или МНН - покажем, в каких аптеках вашей зоны он есть, сколько его, когда аптека работает и ближайший срок годности."
      />
    );
  } else if (!data) {
    body = loading ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={28} /></Box> : null;
  } else if (!found) {
    body = (
      <EditEmptyState
        icon={<NotFoundIcon />} title="Ничего не найдено"
        text={`По запросу «${data.term}» товаров нет. Проверьте написание или поищите по МНН или штрихкоду.`}
      />
    );
  } else {
    body = (
      <>
        <RemainsFilters
          filters={filters} regions={regions} originPharmacies={origins} origin={origin}
          self={self} selfError={meta.selfError || null}
          withContacts={withContacts} selectedCount={selected.length}
          onChange={patchFilters} onLocate={locate} onOriginPharmacy={pickOrigin} onClearSelected={clearSelected} onReset={resetFilters}
        />

        {data.truncated && (
          <Alert severity="info" sx={{ py: 0.25 }}>
            Показаны первые {fmtInt(data.limit)} товаров - уточните запрос, чтобы увидеть остальные.
          </Alert>
        )}
        {data.warnings.map(w => <Alert key={w} severity="warning" sx={{ py: 0.25 }}>{w}</Alert>)}

        <RemainsSummary
          summary={summary} loading={loading} withSums={withSums} withContacts={withContacts} withOrigin={hasOrigin}
          onOpenPharmacy={setOpenGuid}
        />

        <Box sx={{ borderBottom: '1px solid #e2e8f0' }}>
          <Tabs value={tab} onChange={changeTab} sx={tabsSx}>
            <Tab value="pharmacies" label={`Аптеки (${fmtInt(pharmacyRows.length)})`} />
            <Tab value="products" label={`Товары (${fmtInt(data.products.length)})`} />
            <Tab value="map" label="Карта" />
          </Tabs>
        </Box>

        <Box sx={{ display: tab === 'pharmacies' ? 'block' : 'none' }}>
          <PharmaciesTable
            rows={pharmacyRows} total={selectedRows.length} sort={pharmacySort} loading={loading}
            withSums={withSums} withContacts={withContacts} origin={origin} hasFilters={hasListFilters}
            onSort={sortPharmacies} onOpen={setOpenGuid} onResetFilters={resetFilters}
          />
        </Box>

        <Box sx={{ display: tab === 'products' ? 'block' : 'none' }}>
          <ProductsPanel
            rows={productRows} total={productRowsAll.length} folders={folders} folder={folder} sort={productSort} selected={selected}
            loading={loading} withSums={withSums} withKm={withKm}
            onFolder={setFolder} onSort={sortProducts} onToggle={toggleProduct} onSelect={setSelected} onClear={clearSelected}
          />
        </Box>

        {/* карта строится только на своей вкладке: в скрытом блоке Leaflet неверно считает размеры */}
        {tab === 'map' && (
          <RemainsMap rows={pharmacyRows} origin={origin} radius={filters.radius} withSums={withSums} onOpen={setOpenGuid} />
        )}
      </>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, width: '100%' }}>
      <DataFreshness meta={meta} refreshing={refreshing} onRefresh={refreshData} />

      <RemainsToolbar
        meta={meta} loading={loading} canExport={Boolean(found && !search.stale && data && data.stocks.length)} exporting={exporting}
        onSearch={runSearch} onExport={exportExcel}
      />

      {search.error && !loading && (
        <Alert
          severity={search.error.forbidden ? 'warning' : 'error'}
          action={search.error.forbidden ? undefined : <Button color="inherit" size="small" onClick={search.retry}>Повторить</Button>}
        >
          {search.error.text}
        </Alert>
      )}

      {body}

      <PharmacyDialog guid={openGuid} data={data} selected={selected} origin={origin} meta={meta} onClose={closePharmacy} />
    </Box>
  );
};

export default RemainsModule;
